import React from 'react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  MousePointer,
  Move,
  RotateCw,
  Maximize2,
  Grid3x3,
  Box,
  Eye,
  EyeOff,
  Save,
  FolderOpen,
  Play,
  Pause,
} from 'lucide-react';
import { useUIStore, useSceneStore, useMockDataStore } from '../../stores';
import { cn } from '@/lib/utils';

interface ToolbarButtonProps {
  icon: React.ComponentType<{ className?: string }>;
  tooltip: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  shortcut?: string;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  icon: Icon,
  tooltip,
  active = false,
  disabled = false,
  onClick,
  shortcut,
}) => {
  return (
    <Button
      variant={active ? 'default' : 'ghost'}
      size="sm"
      disabled={disabled}
      onClick={onClick}
      className={cn('h-9 w-9 p-0', active && 'bg-blue-600 text-white hover:bg-blue-700')}
      title={shortcut ? `${tooltip} (${shortcut})` : tooltip}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
};

const ViewModeSelector: React.FC = () => {
  const { viewMode, setViewMode } = useUIStore();

  const viewModes = [
    { type: 'editor', label: 'Editor', icon: '✏️' },
    { type: 'preview', label: 'Preview', icon: '👁️' },
    { type: 'heatmap', label: 'Heatmap', icon: '🔥' },
    { type: 'blueprint', label: 'Blueprint', icon: '📐' },
    { type: 'data', label: 'Data', icon: '📊' },
  ];

  return (
    <div className="flex items-center space-x-1 bg-white dark:bg-gray-800 rounded-md p-1">
      {viewModes.map((mode) => (
        <Button
          key={mode.type}
          variant={viewMode.type === mode.type ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setViewMode({ type: mode.type as any })}
          className="h-8 px-3 text-xs"
        >
          <span className="mr-1">{mode.icon}</span>
          {mode.label}
        </Button>
      ))}
    </div>
  );
};

export const Toolbar: React.FC = () => {
  const { editorMode, setEditorMode, viewMode, setViewMode } = useUIStore();
  const { currentScene, updateSettings, saveToLocalStorage } = useSceneStore();
  const { realTimeEnabled, toggleRealTime } = useMockDataStore();

  const handleModeChange = (mode: 'select' | 'translate' | 'rotate' | 'scale') => {
    setEditorMode({ mode });
  };

  const toggleGrid = () => {
    if (currentScene) {
      updateSettings({ showGrid: !currentScene.settings.showGrid });
    }
  };

  const toggleWireframe = () => {
    setViewMode({ showWireframe: !viewMode.showWireframe });
  };

  const toggleVisibility = () => {
    setViewMode({ showLabels: !viewMode.showLabels });
  };

  return (
    <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10">
      <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-2">
        {/* Transform tools */}
        <div className="flex items-center space-x-1">
          <ToolbarButton
            icon={MousePointer}
            tooltip="Select"
            active={editorMode.mode === 'select'}
            onClick={() => handleModeChange('select')}
            shortcut="V"
          />
          <ToolbarButton
            icon={Move}
            tooltip="Move"
            active={editorMode.mode === 'translate'}
            onClick={() => handleModeChange('translate')}
            shortcut="W"
          />
          <ToolbarButton
            icon={RotateCw}
            tooltip="Rotate"
            active={editorMode.mode === 'rotate'}
            onClick={() => handleModeChange('rotate')}
            shortcut="E"
          />
          <ToolbarButton
            icon={Maximize2}
            tooltip="Scale"
            active={editorMode.mode === 'scale'}
            onClick={() => handleModeChange('scale')}
            shortcut="R"
          />
        </div>

        <Separator orientation="vertical" className="h-6" />

        {/* Display options */}
        <div className="flex items-center space-x-1">
          <ToolbarButton
            icon={Grid3x3}
            tooltip="Toggle Grid"
            active={currentScene?.settings.showGrid}
            onClick={toggleGrid}
            shortcut="G"
          />
          <ToolbarButton
            icon={Box}
            tooltip="Toggle Wireframe"
            active={viewMode.showWireframe}
            onClick={toggleWireframe}
            shortcut="Z"
          />
          <ToolbarButton
            icon={viewMode.showLabels ? Eye : EyeOff}
            tooltip="Toggle Labels"
            active={viewMode.showLabels}
            onClick={toggleVisibility}
            shortcut="H"
          />
        </div>

        <Separator orientation="vertical" className="h-6" />

        {/* View modes */}
        <ViewModeSelector />

        <Separator orientation="vertical" className="h-6" />

        {/* Actions */}
        <div className="flex items-center space-x-1">
          <ToolbarButton icon={Save} tooltip="Save Scene" onClick={saveToLocalStorage} shortcut="Ctrl+S" />
          <ToolbarButton
            icon={FolderOpen}
            tooltip="Load Scene"
            onClick={() => {
              /* TODO: Implement load scene dialog */
            }}
            shortcut="Ctrl+O"
          />
          <ToolbarButton
            icon={realTimeEnabled ? Pause : Play}
            tooltip={realTimeEnabled ? 'Pause Real-time Data' : 'Start Real-time Data'}
            active={realTimeEnabled}
            onClick={toggleRealTime}
          />
        </div>
      </div>
    </div>
  );
};
